import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from './Logo';
import { Toaster } from 'react-hot-toast';
import { LayoutDashboard, Users, FileText, Mail, Activity, CheckSquare } from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    if (!user) return [];

    switch (user.role) {
      case 'broker':
        return [
          { path: '/checklist', label: 'Checklist', icon: CheckSquare },
        ];
      case 'inspector':
        return [
          { path: '/inspector', label: 'Dashboard', icon: LayoutDashboard },
        ];
      case 'admin':
        return [
          { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
          { path: '/admin/cycles', label: 'Cycles', icon: Activity },
          { path: '/admin/firms', label: 'Firms', icon: Users },
          { path: '/admin/users', label: 'Users', icon: Users },
          { path: '/admin/submissions', label: 'Submissions', icon: FileText },
          { path: '/admin/email-logs', label: 'Email Logs', icon: Mail },
          { path: '/admin/audit-logs', label: 'Audit Logs', icon: Activity },
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-offWhite">
      {/* Header */}
      <header className="bg-dark shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                <Logo variant="sm" className="bg-white rounded p-1" />
                <span className="text-white font-semibold text-lg hidden sm:inline">Broker Inspection Portal</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-white text-sm">
                {user?.firstName} {user?.lastName}
              </span>
              <button
                onClick={handleLogout}
                className="text-white hover:text-primary transition-colors text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      {navItems.length > 0 && (
        <nav className="bg-white border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors flex items-center space-x-2 ${
                      location.pathname === item.path || (location.pathname.startsWith(item.path) && item.path !== '/admin' && item.path !== '/inspector')
                        ? 'border-primary text-primary'
                        : 'border-transparent text-grey hover:text-primary hover:border-gray-300'
                    }`}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
      
      <Toaster position="top-right" />
    </div>
  );
};

export default Layout;
